import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useIdentity } from '../contexts/IdentityContext';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import { gemsApi } from '../lib/api';
import GemCard from '../components/GemCard';
import CreateGemDialog from '../components/CreateGemDialog';
import NotificationsPanel from '../components/NotificationsPanel';
import { Plus, Gem, Sparkles, Clock, Heart, ArrowUpDown, ThumbsUp, Bell } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';

export default function HomePage() {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { anonId } = useIdentity();
  const { isAuthenticated, user } = useAuth();
  const { totalUnread, markCommentsRead } = useNotifications();
  const [gems, setGems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [sortBy, setSortBy] = useState('newest');
  const [activeTab, setActiveTab] = useState('all'); // 'all' or 'liked'

  const fetchGems = useCallback(async () => {
    try {
      setLoading(true);
      const res = await gemsApi.getGems(null, 1, 50, null, sortBy);
      setGems(res.data.gems || []);
    } catch (err) {
      console.error('Failed to fetch gems:', err);
      toast.error(t('failed_load_gems') || 'Failed to load gems');
    } finally {
      setLoading(false);
    }
  }, [sortBy]);

  useEffect(() => {
    fetchGems();
  }, [fetchGems]);

  const handleLike = async (gemId) => {
    try {
      const res = await gemsApi.toggleLike(gemId, anonId);
      setGems(prev => prev.map(g => {
        if (g.id === gemId) {
          const liked = res.data.action === 'liked';
          return {
            ...g,
            likes_count: res.data.likes_count,
            liked_by: liked
              ? [...(g.liked_by || []), anonId]
              : (g.liked_by || []).filter(id => id !== anonId)
          };
        }
        return g;
      }));
    } catch (err) {
      toast.error(t('failed_update_like') || 'Failed to update like');
    }
  };

  const handleReport = async (gemId) => {
    try {
      await gemsApi.reportGem(gemId, anonId, 'Inappropriate content');
      toast.success(t('gem_reported'));
    } catch (err) {
      toast.error(t('failed_report') || 'Failed to report gem');
    }
  };

  const handleDelete = async (gemId) => {
    try {
      await gemsApi.deleteGem(gemId, anonId);
      setGems(prev => prev.filter(g => g.id !== gemId));
      toast.success(t('gem_deleted'));
    } catch (err) {
      toast.error(t('failed_delete_gem') || 'Failed to delete gem');
    }
  };

  const handleShare = async (gem) => {
    let text = '';
    if (gem.verse_ref) text += `${gem.verse_ref}\n\n`;
    if (gem.text) text += `"${gem.text}"\n\n`;
    if (gem.insight) text += `${gem.insight}\n\n`;
    text += '— Spiritual Gems';
    if (navigator.share) {
      try {
        await navigator.share({ title: 'Spiritual Gem', text });
      } catch (e) {
        if (e.name !== 'AbortError') {
          navigator.clipboard.writeText(text);
          toast.success(t('copied'));
        }
      }
    } else {
      navigator.clipboard.writeText(text);
      toast.success(t('copied'));
    }
  };

  const handleGemCreated = (newGem) => {
    setGems(prev => [newGem, ...prev]);
    setShowCreate(false);
    toast.success(t('gem_posted'));
  };

  // Handle message user click - navigate to messages with state
  const handleMessageUser = (userId, userName) => {
    if (!isAuthenticated) {
      toast.error(t('login_to_message') || 'Please log in to send messages');
      return;
    }
    if (userId === user?.id) {
      toast.error(t('cannot_message_self') || "You can't message yourself");
      return;
    }
    // Store the recipient info in sessionStorage for the MessagesPage to use
    sessionStorage.setItem('sg_message_recipient', JSON.stringify({ id: userId, name: userName }));
    navigate('/messages');
  };

  return (
    <div className="px-4 pt-4 pb-4">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-[var(--font-heading)] tracking-[-0.01em] text-foreground flex items-center gap-2">
            <Gem className="w-5 h-5 text-primary" />
            {t('app_name')}
          </h1>
          <div className="flex items-center gap-2">
            {/* Notification Bell */}
            {isAuthenticated && (
              <button
                onClick={() => setShowNotifications(true)}
                className="relative p-2 rounded-xl hover:bg-accent transition-colors duration-200"
                data-testid="notifications-bell"
              >
                <Bell className="w-5 h-5 text-muted-foreground" />
                {totalUnread > 0 && (
                  <motion.span
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center bg-destructive text-destructive-foreground text-[10px] font-bold rounded-full px-1"
                    data-testid="notifications-badge"
                  >
                    {totalUnread > 99 ? '99+' : totalUnread}
                  </motion.span>
                )}
              </button>
            )}
            <button
              onClick={() => {
                if (!isAuthenticated) { toast.info(t('login_to_post') || 'Please log in to post'); return; }
                setShowCreate(true);
              }}
              className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-primary text-primary-foreground text-xs font-medium shadow-[0_6px_18px_-10px_hsl(var(--shadow-color)/0.6)] hover:bg-primary/90 transition-colors duration-200"
              data-testid="gems-share-button"
            >
              <Sparkles className="w-3.5 h-3.5" />
              {t('create_gem')}
            </button>
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-1">{t('welcome_subtitle')}</p>
      </div>

      {/* Tabs: All Gems / Liked Gems */}
      <div className="flex items-center gap-2 mb-4 border-b border-border">
        <button
          onClick={() => setActiveTab('all')}
          className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium transition-colors relative ${
            activeTab === 'all'
              ? 'text-primary'
              : 'text-muted-foreground hover:text-foreground'
          }`}
          data-testid="tab-all-gems"
        >
          <Gem className="w-4 h-4" />
          {t('app_name')}
          {activeTab === 'all' && (
            <motion.div
              layoutId="activeTab"
              className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary"
              initial={false}
              transition={{ type: "spring", stiffness: 500, damping: 30 }}
            />
          )}
        </button>
        <button
          onClick={() => setActiveTab('liked')}
          className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium transition-colors relative ${
            activeTab === 'liked'
              ? 'text-primary'
              : 'text-muted-foreground hover:text-foreground'
          }`}
          data-testid="tab-liked-gems"
        >
          <Heart className="w-4 h-4" />
          {t('liked_gems')}
          {activeTab === 'liked' && (
            <motion.div
              layoutId="activeTab"
              className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary"
              initial={false}
              transition={{ type: "spring", stiffness: 500, damping: 30 }}
            />
          )}
        </button>
      </div>

      {/* Sort Filter */}
      <div className="flex items-center gap-1.5 mb-4" data-testid="gems-sort-filter">
        {[
          { value: 'newest', icon: Clock, label: t('sort_newest') },
          { value: 'oldest', icon: ArrowUpDown, label: t('sort_oldest') },
          { value: 'most_liked', icon: Heart, label: t('sort_most_liked') },
        ].map((option) => (
          <button
            key={option.value}
            onClick={() => setSortBy(option.value)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors duration-200 ${
              sortBy === option.value
                ? 'bg-primary text-primary-foreground shadow-sm'
                : 'bg-muted text-muted-foreground hover:bg-accent'
            }`}
            data-testid={`gems-sort-${option.value}`}
          >
            <option.icon className="w-3 h-3" />
            {option.label}
          </button>
        ))}
      </div>

      {/* Gems Feed */}
      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="rounded-[var(--radius)] border border-border bg-card p-4 animate-pulse">
              <div className="h-4 bg-muted rounded w-3/4 mb-3" />
              <div className="h-3 bg-muted rounded w-full mb-2" />
              <div className="h-3 bg-muted rounded w-2/3" />
            </div>
          ))}
        </div>
      ) : (() => {
        // Filter gems based on active tab
        const filteredGems = activeTab === 'liked' 
          ? gems.filter(gem => gem.liked_by && gem.liked_by.includes(anonId))
          : gems;
        
        return filteredGems.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-16"
          >
            <div className="w-16 h-16 rounded-2xl bg-accent/50 flex items-center justify-center mx-auto mb-4">
              {activeTab === 'liked' ? (
                <Heart className="w-8 h-8 text-muted-foreground" />
              ) : (
                <Gem className="w-8 h-8 text-muted-foreground" />
              )}
            </div>
            <p className="text-muted-foreground text-sm" data-testid="no-gems-message">
              {activeTab === 'liked' ? "You haven't liked any gems yet" : t('no_gems')}
            </p>
          </motion.div>
        ) : (
          <div className="space-y-4">
            <AnimatePresence mode="popLayout">
              {filteredGems.map((gem) => (
                <motion.div
                  key={gem.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.25 }}
                  layout
                >
                <GemCard
                  gem={gem}
                  anonId={anonId}
                  onLike={handleLike}
                  onShare={handleShare}
                  onReport={handleReport}
                  onDelete={handleDelete}
                  onMessageUser={handleMessageUser}
                  userId={user?.id}
                  userName={user?.pseudonym}
                />
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
        );
      })()}

      {/* FAB */}
      <motion.button
        whileTap={{ scale: 0.95 }}
        onClick={() => setShowCreate(true)}
        className="fixed right-4 bottom-[calc(80px+env(safe-area-inset-bottom,0px))] z-30 h-14 w-14 rounded-full bg-primary text-primary-foreground shadow-[0_10px_30px_-12px_hsl(var(--shadow-color)/0.7)] hover:bg-primary/90 flex items-center justify-center transition-colors duration-200"
        data-testid="gems-create-fab"
      >
        <Plus className="w-6 h-6" />
      </motion.button>

      {/* Create Gem Dialog */}
      <CreateGemDialog
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={handleGemCreated}
      />

      {/* Notifications Panel */}
      <NotificationsPanel
        open={showNotifications}
        onClose={() => setShowNotifications(false)}
      />
    </div>
  );
}
